import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
} from "react";
import {
  AppState,
  Language,
  Shop,
  AppSettings,
  Tab,
  RoutePoint,
  Event,
} from "../types";
import { apiService, setBackendUnavailableCallback } from "../services/api";
import { useAppSettings } from "../hooks/useAppSettings";

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  setLanguage: (language: Language) => void;
  setScreensaverActive: (active: boolean) => void;
  setCurrentFloor: (floor: number) => void;
  setSelectedShop: (shop?: Shop) => void;
  setSearchQuery: (query: string) => void;
  setSearchResults: (results: Shop[]) => void;
  setSearchOpen: (open: boolean) => void;
  resetActivity: () => void;
  refreshAppSettings: () => Promise<void>;
  setActiveTabId: (tabId: string | null) => void;
  setActiveRoutePoints: (points?: RoutePoint[]) => void;
  setBackendUnavailable: (unavailable: boolean) => void;
  setSelectedShopReload: (shop?: Shop) => void;
  setTerminalName: (name?: string) => void;
  setCurrentFloorReload: (floor?: number) => void;
  setAccessibilityMode: (enabled: boolean) => void;
  setSelectedEvent: (event?: Event) => void;
}

type AppAction =
  | { type: "SET_LANGUAGE"; payload: Language }
  | { type: "SET_SCREENSAVER_ACTIVE"; payload: boolean }
  | { type: "SET_CURRENT_FLOOR"; payload: number }
  | { type: "SET_SELECTED_SHOP"; payload: Shop | undefined }
  | { type: "SET_SEARCH_QUERY"; payload: string }
  | { type: "SET_SEARCH_RESULTS"; payload: Shop[] }
  | { type: "SET_SEARCH_OPEN"; payload: boolean }
  | { type: "RESET_ACTIVITY" }
  | { type: "SET_APP_SETTINGS"; payload: AppSettings }
  | { type: "SET_TABS"; payload: Tab[] }
  | { type: "SET_ACTIVE_TAB_ID"; payload: string | null }
  | { type: "SET_ACTIVE_ROUTE_POINTS"; payload: RoutePoint[] | undefined }
  | { type: "SET_BACKEND_UNAVAILABLE"; payload: boolean }
  | { type: "SET_SELECTED_SHOP_RELOAD"; payload: Shop | undefined }
  | { type: "SET_TERMINAL_NAME"; payload: string | undefined }
  | { type: "SET_CURRENT_FLOOR_RELOAD"; payload: number | undefined }
  | { type: "SET_ACCESSIBILITY_MODE"; payload: boolean }
  | { type: "SET_SELECTED_EVENT"; payload: Event | undefined };

const initialState: AppState = {
  language: "ru",
  isScreensaverActive: false,
  lastActivity: Date.now(),
  currentFloor: 1,
  selectedShop: undefined,
  searchQuery: "",
  searchResults: [],
  isSearchOpen: false,
  activeTabId: null,
  activeRoutePoints: undefined,
  isBackendUnavailable: false,
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case "SET_LANGUAGE":
      return { ...state, language: action.payload };
    case "SET_SCREENSAVER_ACTIVE":
      return { ...state, isScreensaverActive: action.payload };
    case "SET_CURRENT_FLOOR":
      return { ...state, currentFloor: action.payload };
    case "SET_SELECTED_SHOP":
      return {
        ...state,
        selectedShop: action.payload,
      };
    case "SET_SEARCH_QUERY":
      return { ...state, searchQuery: action.payload };
    case "SET_SEARCH_RESULTS":
      return { ...state, searchResults: action.payload };
    case "SET_SEARCH_OPEN":
      return { ...state, isSearchOpen: action.payload };
    case "RESET_ACTIVITY":
      return { ...state, lastActivity: Date.now() };
    case "SET_APP_SETTINGS":
      return { ...state, appSettings: action.payload };
    case "SET_TABS":
      return { ...state, tabs: action.payload };
    case "SET_ACTIVE_TAB_ID":
      return { ...state, activeTabId: action.payload };
    case "SET_ACTIVE_ROUTE_POINTS":
      return { ...state, activeRoutePoints: action.payload };
    case "SET_BACKEND_UNAVAILABLE":
      return { ...state, isBackendUnavailable: action.payload };
    case "SET_SELECTED_SHOP_RELOAD":
      return { ...state, selectedShopReload: action.payload };
    case "SET_TERMINAL_NAME":
      return { ...state, terminalName: action.payload };
    case "SET_CURRENT_FLOOR_RELOAD":
      return { ...state, currentFloorReload: action.payload };
    case "SET_ACCESSIBILITY_MODE":
      return { ...state, isAccessibilityMode: action.payload };
    case "SET_SELECTED_EVENT":
      return { ...state, selectedEvent: action.payload };
    default:
      return state;
  }
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const prevScreensaverActiveRef = useRef<boolean>(false);

  // Load app settings using TanStack Query for automatic updates
  const { data: appSettings, refetch: refetchSettings } = useAppSettings();

  // Update app settings in state when they change
  useEffect(() => {
    if (appSettings) {
      console.log("Loaded app settings:", appSettings);
      dispatch({ type: "SET_APP_SETTINGS", payload: appSettings });
    }
  }, [appSettings]);

  // Set up backend unavailable callback
  useEffect(() => {
    setBackendUnavailableCallback((unavailable: boolean) => {
      dispatch({ type: "SET_BACKEND_UNAVAILABLE", payload: unavailable });
    });
  }, []);

  // Periodic health check when backend is unavailable
  useEffect(() => {
    if (!state.isBackendUnavailable) return;

    const healthCheck = async () => {
      try {
        await apiService.getAppSettings();
        // If successful, backend is available again - reload the page
        window.location.reload();
      } catch (error) {
        // Backend still unavailable, keep showing error page
      }
    };

    const interval = setInterval(healthCheck, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [state.isBackendUnavailable]);

  // Load saved language from localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem("language") as Language;
    if (savedLanguage && (savedLanguage === "ru" || savedLanguage === "en")) {
      dispatch({ type: "SET_LANGUAGE", payload: savedLanguage });
    }
  }, []);

  // Load tabs
  useEffect(() => {
    const loadTabs = async () => {
      try {
        const tabs = await apiService.getTabs();
        console.log("Loaded tabs:", tabs);
        dispatch({ type: "SET_TABS", payload: tabs });
      } catch (error) {
        console.error("Failed to load tabs:", error);
      }
    };
    loadTabs();
  }, []);

  // Listen for tabs updates (e.g., after deletion)
  useEffect(() => {
    const handleTabsUpdate = async () => {
      try {
        const tabs = await apiService.getTabs();
        console.log("Tabs updated, reloading:", tabs);
        const currentActiveTabId = state.activeTabId;

        // Check if the currently active tab still exists
        const activeTabExists = tabs.some(
          (tab) => tab.id === currentActiveTabId
        );

        // If active tab was deleted, switch to first available tab or null
        if (currentActiveTabId && !activeTabExists) {
          if (tabs.length > 0) {
            dispatch({ type: "SET_ACTIVE_TAB_ID", payload: tabs[0].id });
          } else {
            dispatch({ type: "SET_ACTIVE_TAB_ID", payload: null });
          }
        }

        dispatch({ type: "SET_TABS", payload: tabs });
      } catch (error) {
        console.error("Failed to reload tabs:", error);
      }
    };

    window.addEventListener("tabsUpdated", handleTabsUpdate);
    return () => {
      window.removeEventListener("tabsUpdated", handleTabsUpdate);
    };
  }, [state.activeTabId]);

  // Screensaver timeout (configurable from settings)
  useEffect(() => {
    const screensaverTimeout =
      state.appSettings?.screensaverTimeout || 5 * 60 * 1000; // Default 5 minutes
    const interval = setInterval(() => {
      const now = Date.now();
      if (
        now - state.lastActivity > screensaverTimeout &&
        !state.isScreensaverActive
      ) {
        dispatch({ type: "SET_SCREENSAVER_ACTIVE", payload: true });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [
    state.lastActivity,
    state.isScreensaverActive,
    state.appSettings?.screensaverTimeout,
  ]);

  // Mouse activity tracking with throttling
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let lastActivityTime = 0;

    const handleActivity = () => {
      const now = Date.now();

      // Only update if enough time has passed (throttle more aggressively)
      if (now - lastActivityTime < 500) {
        return;
      }

      lastActivityTime = now;

      // Throttle activity updates to prevent excessive re-renders
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        dispatch({ type: "RESET_ACTIVITY" });
      }, 200); // Throttle to max 5 updates per second
    };

    // Add event listeners for user activity
    document.addEventListener("mousemove", handleActivity, { passive: true });
    document.addEventListener("mousedown", handleActivity, { passive: true });
    document.addEventListener("keydown", handleActivity, { passive: true });
    document.addEventListener("touchstart", handleActivity, { passive: true });

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener("mousemove", handleActivity);
      document.removeEventListener("mousedown", handleActivity);
      document.removeEventListener("keydown", handleActivity);
      document.removeEventListener("touchstart", handleActivity);
    };
  }, []);

  // Clear activeRoutePoints when selectedShop changes
  useEffect(() => {
    dispatch({ type: "SET_ACTIVE_ROUTE_POINTS", payload: undefined });
  }, [state.selectedShop?.id]);

  // Reload page when exiting screensaver (covers any exit path)
  useEffect(() => {
    if (prevScreensaverActiveRef.current && !state.isScreensaverActive) {
      window.location.reload();
    }
    prevScreensaverActiveRef.current = state.isScreensaverActive;
  }, [state.isScreensaverActive]);

  const setLanguage = (language: Language) => {
    localStorage.setItem("language", language);
    dispatch({ type: "SET_LANGUAGE", payload: language });
  };

  const setScreensaverActive = (active: boolean) => {
    dispatch({ type: "SET_SCREENSAVER_ACTIVE", payload: active });
    if (!active) {
      dispatch({ type: "RESET_ACTIVITY" });
    }
  };

  const setCurrentFloor = (floor: number) => {
    dispatch({ type: "SET_CURRENT_FLOOR", payload: floor });
  };

  const setSelectedShop = (shop?: Shop) => {
    dispatch({ type: "SET_SELECTED_SHOP", payload: shop });
  };

  const setSearchQuery = (query: string) => {
    dispatch({ type: "SET_SEARCH_QUERY", payload: query });
  };

  const setSearchResults = (results: Shop[]) => {
    dispatch({ type: "SET_SEARCH_RESULTS", payload: results });
  };

  const setSearchOpen = (open: boolean) => {
    dispatch({ type: "SET_SEARCH_OPEN", payload: open });
  };

  const resetActivity = () => {
    dispatch({ type: "RESET_ACTIVITY" });
  };

  const setActiveRoutePoints = (points?: RoutePoint[]) => {
    dispatch({ type: "SET_ACTIVE_ROUTE_POINTS", payload: points });
  };

  const setActiveTabId = (tabId: string | null) => {
    dispatch({ type: "SET_ACTIVE_TAB_ID", payload: tabId });
  };

  const setBackendUnavailable = (unavailable: boolean) => {
    dispatch({ type: "SET_BACKEND_UNAVAILABLE", payload: unavailable });
  };

  const setSelectedShopReload = (shop?: Shop) => {
    dispatch({ type: "SET_SELECTED_SHOP_RELOAD", payload: shop });
  };

  const setTerminalName = (name?: string) => {
    dispatch({ type: "SET_TERMINAL_NAME", payload: name });
  };

  const setCurrentFloorReload = (floor?: number) => {
    dispatch({ type: "SET_CURRENT_FLOOR_RELOAD", payload: floor });
  };

  const setAccessibilityMode = (enabled: boolean) => {
    dispatch({ type: "SET_ACCESSIBILITY_MODE", payload: enabled });
  };

  const setSelectedEvent = (event?: Event) => {
    dispatch({ type: "SET_SELECTED_EVENT", payload: event });
  };

  const refreshAppSettings = async () => {
    // Settings are now automatically refreshed via TanStack Query
    // This function triggers a manual refetch
    try {
      const result = await refetchSettings();
      if (result.data) {
        console.log("Refreshed app settings:", result.data);
        dispatch({ type: "SET_APP_SETTINGS", payload: result.data });
      }
    } catch (error) {
      console.error("Failed to refresh app settings:", error);
    }
  };

  const value: AppContextType = {
    state,
    dispatch,
    setLanguage,
    setScreensaverActive,
    setCurrentFloor,
    setSelectedShop,
    setSearchQuery,
    setSearchResults,
    setSearchOpen,
    resetActivity,
    refreshAppSettings,
    setActiveTabId,
    setActiveRoutePoints,
    setBackendUnavailable,
    setSelectedShopReload,
    setTerminalName,
    setCurrentFloorReload,
    setAccessibilityMode,
    setSelectedEvent,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}
